import styles from './area-panel.module.scss';
import { Add, TrashCan } from '@carbon/react/icons';
import {
  Button,
  InlineLoading,
  Modal,
  Search,
  SkeletonPlaceholder,
  Layer,
  Tag,
  Tooltip,
} from '@carbon/react';
import { useScenarioForm, SCENARIO_DETAILS_ID } from './contexts';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createResourceAtom } from 'lib/atoms';
import { debounce } from 'lodash-es';
import classNames from 'classnames';
import { AreaConfigResponse, AreaConfigsResponse } from './types';
import { INITIAL_AREA_CONFIG } from './default-values';
import { deleteAreaConfigAtom } from './atoms';
import { buildConfigDisplayName } from './utils';
import { useParams } from 'react-router-dom';

const FALLBACK_ATOM = atom(null);

interface AreaPanelProps {
  resourceUrl?: string;
}

interface DeleteConfigModalState {
  open: boolean;
  href: string | null;
  name: string | null;
}

interface PreventAddNewState {
  open: boolean;
  id: string | null;
}

export const AreaPanel = ({ resourceUrl }: AreaPanelProps) => {
  const {
    form,
    isFormModified,
    resetConfigId,
    setSelectedConfigId,
    setAreaConfigs,
    areaConfigs,
    selectedConfigId,
    isScenarioDetailsSelected,
    handleSubmitForm,
    setDiscardCount,
    readOnly,
    setSubAreaGeometries,
  } = useScenarioForm();
  const { id: scenarioId = null } = useParams<{ id?: string }>();
  const [searchText, setSearchText] = useState<string>('');
  const [filteredAreaConfigs, setFilteredAreaConfigs] = useState<
    AreaConfigResponse[] | null
  >(null);
  const [preventAddNewState, setPreventAddNewState] =
    useState<PreventAddNewState>({
      open: false,
      id: null,
    });
  const [deleteConfigModalState, setDeleteConfigModalState] =
    useState<DeleteConfigModalState>({
      open: false,
      href: null,
      name: null,
    });
  const [deleteAreaConfigState, runDeleteAreaConfig] =
    useAtom(deleteAreaConfigAtom);

  const areaConfigsAtom = useMemo(() => {
    if (!resourceUrl) return FALLBACK_ATOM;
    return createResourceAtom<AreaConfigsResponse>(resourceUrl);
  }, [resourceUrl]);

  const areaConfigsResponse = useAtomValue(areaConfigsAtom);

  useEffect(() => {
    if (areaConfigsResponse === null) {
      // New scenario — no area configs fetched, start on Scenario Details
      setAreaConfigs([]);
      setSelectedConfigId(SCENARIO_DETAILS_ID);
    } else if (
      areaConfigsResponse.data &&
      'results' in areaConfigsResponse.data
    ) {
      // Stamp each loaded config with a UI-stable elementId. Using the
      // server id as the seed keeps the value deterministic across reloads,
      // which is fine because a saved config's id never changes after this.
      const results = areaConfigsResponse.data.results.map((config) => ({
        ...config,
        elementId: String(config.id),
      }));
      setAreaConfigs(results);
      setSelectedConfigId(SCENARIO_DETAILS_ID);
    }
  }, [areaConfigsResponse, setAreaConfigs, setSelectedConfigId]);

  const debouncedFilteredOptions = useMemo(() => {
    return debounce((text: string) => {
      if (!text) return setFilteredAreaConfigs(null);

      const filtered = areaConfigs.filter((config) => {
        const name = buildConfigDisplayName(config);
        return name.toLowerCase().includes(text.toLowerCase());
      });

      setFilteredAreaConfigs(filtered);
    }, 300);
  }, [areaConfigs]);

  const handleAddNewConfig = () => {
    const hasUnsaved = areaConfigs.some((config) => !config.href);
    if (hasUnsaved) return;

    const newConfig: AreaConfigResponse = {
      ...INITIAL_AREA_CONFIG,
      id: 'local',
      // Fresh UI-stable id for this slot. Survives the local→server id
      // promotion in handleSubmitForm so the area-configs reset effect
      // doesn't fire on save.
      elementId: crypto.randomUUID(),
    };
    setAreaConfigs((prev) => [...prev, newConfig]);
    resetConfigId('local');
    setDiscardCount((prev) => prev + 1);
  };

  const handleDeleteConfig = (href: string) => {
    runDeleteAreaConfig({
      url: href,
      onSuccess: () => {
        setAreaConfigs((prev) => prev.filter((config) => config.href !== href));
        setDeleteConfigModalState({ open: false, href: null, name: null });
        resetConfigId(SCENARIO_DETAILS_ID);
        setDiscardCount((prev) => prev + 1);
        setSubAreaGeometries({});
      },
    });
  };

  const handleSwitchConfig = (id: string, isModified: boolean) => {
    if (id === selectedConfigId) return;

    if (isModified) {
      setPreventAddNewState({ open: true, id });
    } else {
      resetConfigId(id);
      setDiscardCount((prev) => prev + 1);

      // Remove unsaved local config when switching away from it
      if (selectedConfigId === 'local') {
        setAreaConfigs((prev) => prev.filter((c) => c.id !== 'local'));
      }
    }
  };

  if (areaConfigsResponse && areaConfigsResponse.loading)
    return <AreaPanelSkeleton />;

  return (
    <div>
      <form.Subscribe selector={(state) => isFormModified(state.values)}>
        {(isModified) => (
          <>
            <div className={styles.configsListContainer}>
              <ConfigItem
                name="Scenario Details"
                id={SCENARIO_DETAILS_ID}
                hideDelete
                isActive={isScenarioDetailsSelected}
                onClick={() =>
                  handleSwitchConfig(SCENARIO_DETAILS_ID, isModified)
                }
              />
            </div>

            <div className={styles.searchConfigsContainer}>
              <Layer>
                <Search
                  size="md"
                  value={searchText}
                  labelText="Search"
                  placeholder="Search list below"
                  onChange={(e) => {
                    const text = e.target.value;
                    setSearchText(text);
                    debouncedFilteredOptions(text);
                  }}
                />
              </Layer>

              {!readOnly && (
                <Button
                  kind="primary"
                  size="md"
                  hasIconOnly
                  renderIcon={Add}
                  iconDescription="Add Area"
                  disabled={
                    !scenarioId || areaConfigs.some((config) => !config.href)
                  }
                  onClick={() => {
                    if (isModified) {
                      setPreventAddNewState({ open: true, id: null });
                    } else {
                      handleAddNewConfig();
                    }
                  }}
                />
              )}
            </div>

            <div className={styles.configsListContainer}>
              {(filteredAreaConfigs || areaConfigs).map((config, index) => {
                const name = buildConfigDisplayName(config);
                const isNew = !config.href;

                return (
                  <ConfigItem
                    key={config.id || index}
                    name={name}
                    id={config.id}
                    hideDelete={!config.href || readOnly}
                    isActive={config.id === selectedConfigId}
                    isDeleting={
                      deleteAreaConfigState?.loading &&
                      deleteConfigModalState.href === config.href
                    }
                    isNew={isNew}
                    onDelete={() => {
                      setDeleteConfigModalState({
                        open: true,
                        href: config.href,
                        name,
                      });
                    }}
                    onClick={() => handleSwitchConfig(config.id, isModified)}
                  />
                );
              })}
            </div>
          </>
        )}
      </form.Subscribe>

      {preventAddNewState.open && (
        <PreventAddNewModal
          newConfigId={preventAddNewState.id}
          onClose={() => setPreventAddNewState({ open: false, id: null })}
          onSave={() => {
            handleSubmitForm({
              typeCurveScenarioId: scenarioId,
              areaConfigId:
                selectedConfigId !== 'local' && !isScenarioDetailsSelected
                  ? selectedConfigId
                  : null,
            });
            setPreventAddNewState({ open: false, id: null });
          }}
          onAddNewConfig={handleAddNewConfig}
        />
      )}

      <DeleteModal
        open={deleteConfigModalState.open}
        onClose={() => {
          setDeleteConfigModalState({ open: false, href: null, name: null });
        }}
        onDelete={() => {
          if (!deleteConfigModalState.href) return;
          handleDeleteConfig(deleteConfigModalState.href);
        }}
        title="Delete Area Config"
        description={`Are you sure you want to delete the "${deleteConfigModalState.name}" area config?`}
      />
    </div>
  );
};

interface ConfigItemProps {
  name: string;
  id: string;
  hideDelete?: boolean;
  onDelete?: () => void;
  onClick: () => void;
  isActive?: boolean;
  isDeleting?: boolean;
  isNew?: boolean;
}

const ConfigItem = ({
  name,
  hideDelete = false,
  onDelete,
  onClick,
  isActive = false,
  isDeleting = false,
  isNew = false,
}: ConfigItemProps) => {
  const nameRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = nameRef.current;
    if (el) setIsTruncated(el.scrollHeight > el.clientHeight);
  }, [name]);

  const nameEl = (
    <p ref={nameRef} className={styles.configItemName}>
      {name}
      {isNew && (
        <Tag type="blue" size="sm" className={styles.newBadge}>
          New
        </Tag>
      )}
    </p>
  );

  return (
    <div
      className={classNames(styles.configItem, { [styles.active]: isActive })}
      onClick={onClick}
    >
      {isTruncated ? (
        <Tooltip label={name} align="bottom-left">
          {nameEl}
        </Tooltip>
      ) : (
        nameEl
      )}
      <div className={styles.configItemActions}>
        {!hideDelete && !isDeleting && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete?.();
            }}
          >
            <TrashCan />
          </button>
        )}
        {isDeleting && <InlineLoading />}
      </div>
    </div>
  );
};

const AreaPanelSkeleton = () => {
  return (
    <div className={styles.areaPanelSkeleton}>
      <SkeletonPlaceholder className={styles.configSkeleton} />
      <SkeletonPlaceholder className={styles.configSkeleton} />
      <div className={styles.searchConfigsContainer}>
        <SkeletonPlaceholder className={styles.searchInputSkeleton} />
        <SkeletonPlaceholder className={styles.addButtonSkeleton} />
      </div>
      <SkeletonPlaceholder className={styles.configSkeleton} />
      <SkeletonPlaceholder className={styles.configSkeleton} />
    </div>
  );
};

interface DeleteModalProps {
  onClose: () => void;
  onDelete: () => void;
  title: string;
  description: string;
  open: boolean;
}

const DeleteModal = ({
  onClose,
  onDelete,
  title,
  description,
  open,
}: DeleteModalProps) => {
  return (
    <Modal
      open={open}
      size="md"
      modalHeading={title}
      onRequestClose={onClose}
      onRequestSubmit={onDelete}
      secondaryButtonText="Cancel"
      primaryButtonText="Yes, Delete"
      preventCloseOnClickOutside
      closeButtonLabel="Close"
      aria-label="delete area config"
    >
      <p>{description}</p>
    </Modal>
  );
};

interface PreventAddNewModalProps {
  onClose: () => void;
  onSave: () => void;
  onAddNewConfig: () => void;
  newConfigId: string | null;
}

const PreventAddNewModal = ({
  onClose,
  onSave,
  onAddNewConfig,
  newConfigId,
}: PreventAddNewModalProps) => {
  const {
    discardFormChanges,
    setAreaConfigs,
    selectedConfigId,
    resetConfigId,
  } = useScenarioForm();

  return (
    <Modal
      open
      size="md"
      modalHeading="Unsaved Changes"
      className={styles.preventAddNewModal}
      primaryButtonText="Save Current Config"
      secondaryButtonText="Discard Changes"
      onRequestClose={onClose}
      onRequestSubmit={onSave}
      onSecondarySubmit={() => {
        onClose();

        // Remove unsaved local config when discarding
        if (selectedConfigId === 'local') {
          setAreaConfigs((prev) => prev.filter((c) => c.id !== 'local'));
        }

        // Reset all form values to baselines (also increments discardCount)
        discardFormChanges();

        if (newConfigId) {
          // Switching to an existing config or scenario details
          resetConfigId(newConfigId);
        } else {
          // Adding a new config after discarding
          onAddNewConfig();
        }
      }}
      preventCloseOnClickOutside
      closeButtonLabel="Close"
      aria-label="prevent add new modal"
    >
      <p className={styles.description}>
        Save the current config before switching or all unsaved changes will be
        lost.
      </p>
    </Modal>
  );
};
